import { Box, Button, Flex, Switch, Text, useMantineTheme, Avatar, Badge } from "@mantine/core"
import { Boxscore, VideoEvent } from "../../client"
import React, { useEffect, useRef, useState, useMemo } from "react"
import ReactPlayer from "react-player"
import VideoStats from "../VideoStats/VideoStats"
import { getBoxscore } from "../../api/Games"
import { VideoThumbnail } from "../VideoThumbnail/VideoThumbnail"
import PlayerAvatar from "../Common/PlayerAvatar"
import EventTagBadges from "../Common/EventTagBadges"
import { useLocation } from "react-router-dom"
import { getEventPlayers, isValidPlayer, resolveTargetPlayerId } from "../../utils/player"
import { decodeQueryStack } from "../../models/QueryStack"

import { useInView } from "react-intersection-observer"
import { IconArrowLeft } from "@tabler/icons-react"
import { useHideOutcomes } from "../../hooks/useHideOutcomes"
import { getVideoLeague, parseLeague } from "../../utils/league"
import dayjs from "dayjs"

interface FocusedVideoDisplayProps {
	video: VideoEvent
	videos: VideoEvent[]
	activeSearch: boolean
	onSelect: (video: VideoEvent) => void
  onBack: () => void
  targetPlayerId?: string | null
  targetPlayerName?: string | null
  total?: number
  onLoadMore?: () => void
  hasMore?: boolean
}

interface VideoQueueProps {
	selectedVideo: VideoEvent
	videos: VideoEvent[]
	onSelect: (video: VideoEvent) => void
  total?: number
  onLoadMore?: () => void
  hasMore?: boolean
  maxHeight?: string | number
  targetPlayerId?: string | null
  targetPlayerName?: string | null
}

interface VideoQueueItemProps {
	video: VideoEvent
	isSelected: boolean
	onSelect: (video: VideoEvent) => void
	setItemRef?: (el: HTMLDivElement | null) => void
  targetPlayerId?: string | null
  targetPlayerName?: string | null
}

const getMatchupScoreText = (video: VideoEvent): string | null => {
	const homeAbbv = (video.hAbbv || '').trim()
	const awayAbbv = (video.vAbbv || '').trim()
	const hasScore = Boolean(video.score && video.score !== 'None' && video.score.includes('-'))
	const parts = hasScore ? video.score!.split('-').map((s) => s.trim()) : []
	const validScores = parts.length === 2 && parts[0] !== '' && parts[1] !== ''

	if (homeAbbv && awayAbbv) {
		return validScores ? `${awayAbbv} ${parts[0]} @ ${homeAbbv} ${parts[1]}` : `${awayAbbv} @ ${homeAbbv}`
	}
	if (validScores) return `Away ${parts[0]} @ Home ${parts[1]}`
	if (video.score && video.score !== 'None') return video.score
	return null
}

const getPeriodClockText = (video: VideoEvent): string => {
	const period = video.period < 5 ? `Q${video.period}` : `OT${video.period}`
	return `${period} ${video.clockTime || ''}`.trim()
}

const VideoQueueItem: React.FC<VideoQueueItemProps> = ({ video, isSelected, onSelect, setItemRef, targetPlayerId, targetPlayerName }) => {
	const theme = useMantineTheme()
	const [hideOutcomes] = useHideOutcomes()
	const { ref: inViewRef, inView } = useInView({
		triggerOnce: true,
		rootMargin: "100px",
	})

	const setRefs = (node: HTMLDivElement | null) => {
		if (setItemRef) setItemRef(node)
		inViewRef(node)
	}

	const { primary } = getEventPlayers(video, targetPlayerId, targetPlayerName)
	const matchupScore = getMatchupScoreText(video)
	const periodClock = getPeriodClockText(video)

	return (
		<Flex
			ref={setRefs}
			direction={"row"}
			m={4}
			p={6}
			align={"center"}
			gap={10}
			style={{ borderRadius: 12, cursor: "pointer" }}
			bg={isSelected ? theme.colors.dark[6] : ""}
			onClick={() => onSelect(video)}
		>
			<Box w={96} style={{ flexShrink: 0 }}>
				<VideoThumbnail video={video} inView={inView} compact radius={8} />
			</Box>
			<Box style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
				{/* Row 1: Player Name */}
				<Text fz="xs" fw={700} truncate mb={4}>
					{primary.name}
				</Text>
				{/* Row 2: Timing / Game info */}
				<Flex align="center" gap={4} wrap="wrap" mb={4}>
					{matchupScore && (
						<Badge
							size="xs"
							variant="default"
							radius="sm"
							className={`outcome-content ${hideOutcomes ? "outcome-blur" : ""}`}
						>
							{matchupScore}
						</Badge>
					)}
					<Badge size="xs" variant="default" radius="sm">
						{periodClock}
					</Badge>
				</Flex>
				{/* Row 3: Video Tags */}
				<Box className={`outcome-content ${hideOutcomes ? "outcome-blur" : ""}`}>
					<EventTagBadges
						video={video}
						targetPlayerId={targetPlayerId}
						targetPlayerName={targetPlayerName}
						size="xs"
						maxTags={2}
					/>
				</Box>
			</Box>
		</Flex>
	)
}

const QUEUE_PAGE_SIZE = 10

const VideoQueue: React.FC<VideoQueueProps> = ({
	selectedVideo,
	videos,
	onSelect,
	total,
	onLoadMore,
	hasMore = false,
	maxHeight,
	targetPlayerId,
	targetPlayerName,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map())

	const selectedIndex = videos.findIndex(
		(v) => v.gameId === selectedVideo.gameId && v.eventNumber === selectedVideo.eventNumber
	)
	const initialLimit = selectedIndex >= 0 ? selectedIndex + QUEUE_PAGE_SIZE : QUEUE_PAGE_SIZE
	const [visibleCount, setVisibleCount] = useState<number>(initialLimit)

	useEffect(() => {
		if (selectedIndex >= visibleCount - 2) {
			const nextCount = Math.max(visibleCount, selectedIndex + QUEUE_PAGE_SIZE)
			setVisibleCount(nextCount)
			if (nextCount >= videos.length && hasMore && onLoadMore) {
				onLoadMore()
			}
		}
	}, [selectedIndex, visibleCount, videos.length, hasMore, onLoadMore])

	const displayedVideos = videos.slice(0, visibleCount)

	useEffect(() => {
		const frame = requestAnimationFrame(() => {
			const key = `${selectedVideo.gameId}-${selectedVideo.eventNumber}`
			const itemEl = itemRefs.current.get(key)
			const containerEl = containerRef.current
			if (itemEl && containerEl) {
				const containerRect = containerEl.getBoundingClientRect()
				const itemRect = itemEl.getBoundingClientRect()
				const targetScrollTop = containerEl.scrollTop + (itemRect.top - containerRect.top)
				containerEl.scrollTo({
					top: Math.max(0, targetScrollTop),
					behavior: "smooth",
				})
			}
		})
		return () => cancelAnimationFrame(frame)
	}, [selectedVideo.gameId, selectedVideo.eventNumber, displayedVideos.length])

	const handleLoadMore = () => {
		const nextCount = visibleCount + QUEUE_PAGE_SIZE
		setVisibleCount(nextCount)
		if (nextCount >= videos.length && hasMore && onLoadMore) {
			onLoadMore()
		}
	}

	const effectiveTotal = total !== null && total !== undefined && total > 0 ? total : videos.length
	const remaining = Math.max(0, effectiveTotal - visibleCount)
	const canShowMore = remaining > 0 || hasMore || visibleCount < videos.length

	return (
		<Box
			ref={containerRef}
			style={{
				maxHeight: maxHeight || "100%",
				overflowY: "auto",
			}}
		>
			{displayedVideos.map((video) => {
				const key = `${video.gameId}-${video.eventNumber}`
				return (
					<VideoQueueItem
						key={key}
						video={video}
						isSelected={video === selectedVideo || (video.gameId === selectedVideo.gameId && video.eventNumber === selectedVideo.eventNumber)}
						onSelect={onSelect}
						setItemRef={(el) => {
							if (el) itemRefs.current.set(key, el)
							else itemRefs.current.delete(key)
						}}
						targetPlayerId={targetPlayerId}
						targetPlayerName={targetPlayerName}
					/>
				)
			})}
			{canShowMore ? (
				<Box p={8} ta="center">
					<Button
						variant="subtle"
						size="xs"
						color="gray"
						radius="md"
						fullWidth
						onClick={handleLoadMore}
					>
						Show more clips ({remaining > 0 ? `${remaining} remaining` : 'load next'})
					</Button>
				</Box>
			) : effectiveTotal > 0 ? (
				<Box p={8} ta="center">
					<Text fz="xs" c="dimmed">All {effectiveTotal} clips loaded</Text>
				</Box>
			) : null}
		</Box>
	)
}

const FocusedVideoDisplay: React.FC<FocusedVideoDisplayProps> = ({
	video,
	videos,
	activeSearch,
	onSelect,
	onBack,
	targetPlayerId,
	targetPlayerName,
	total,
	onLoadMore,
	hasMore = false,
}) => {
	const location = useLocation()
	const clipLeague = getVideoLeague(video)
	const fromQuery = new URLSearchParams(location.search).get('from')
	const fromParams = useMemo(
		() => (fromQuery ? new URLSearchParams(decodeURIComponent(fromQuery)) : new URLSearchParams(location.search)),
		[fromQuery, location.search]
	)
	const effectiveLeague = useMemo(() => {
		const urlLeague = fromParams.get('league')
		return urlLeague ? parseLeague(urlLeague) : clipLeague
	}, [fromParams, clipLeague])

	const activeTargetPlayerId = useMemo(() => {
		if (targetPlayerId) return targetPlayerId
		if (fromParams.get('player')) return fromParams.get('player')
		const rawQ = fromParams.get('q')
		if (rawQ) {
			const decodedQueries = decodeQueryStack(rawQ, effectiveLeague, [], [])
			const ids: string[] = []
			const statsList: string[] = []
			decodedQueries.forEach((q) => {
				if (q.type === "player" && q.player?.id) {
					ids.push(String(q.player.id))
				} else if (q.type === "game" && q.players) {
					q.players.forEach((p) => ids.push(String(p.id)))
				}
				if (q.categories) {
					q.categories.forEach((s) => statsList.push(s))
				}
			})
			return resolveTargetPlayerId(video, ids, statsList)
		}
		return resolveTargetPlayerId(video, fromParams.get('players')?.split(','), fromParams.get('stats')?.split(','))
	}, [targetPlayerId, fromParams, effectiveLeague, video])
	const { primary, secondary } = getEventPlayers(video, activeTargetPlayerId, targetPlayerName)
	const hasSecondary = Boolean(secondary && isValidPlayer(secondary.id, secondary.name))
	const matchupScoreText = useMemo(() => getMatchupScoreText(video), [video])
	const periodClockText = useMemo(() => getPeriodClockText(video), [video])
	const dateText = useMemo(() => (video.date ? dayjs(video.date).format('MMM D, YYYY') : null), [video.date])

	const [autoplay, setAutoplay] = useState<boolean>(true)
	const [boxscore, setBoxscore] = useState<Boxscore | null>(null)
	const [playing, setPlaying] = useState(false);
	const [hideOutcomes, setHideOutcomes] = useHideOutcomes();

	useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

	// Getting boxscore info
	useEffect(() => {
		;(async () => {
			const boxscore = await getBoxscore(video.gameId)
			setBoxscore(boxscore)
		})()
	}, [video])

  // Auto-pause when video goes out of view
	const containerRef = useRef(null);
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries[0].isIntersecting) {
					setPlaying(false);
				} else {
					setPlaying(true)
				}
			},
			{ threshold: 0.3 }
		);

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

    return () => observer.disconnect();
  }, []);

  const handleOnVideoEnd = () => {
    const currentIndex = videos.findIndex(
      (v) => v.gameId === video.gameId && v.eventNumber === video.eventNumber
    )
    const nextVideoIdx = currentIndex >= 0 ? currentIndex + 1 : -1
    const nextVideo = nextVideoIdx >= 0 && nextVideoIdx < videos.length ? videos[nextVideoIdx] : null

    if (autoplay && nextVideo) {
      onSelect(nextVideo)
    }
  }

	return (
      <Flex w={"100%"} h={"100%"} direction={{ base: 'column', md: 'row' }}>
				<Flex flex={5} direction={"column"} p={{ base: 'xs', sm: 'md' }}>
					{ activeSearch && (
						<Box mb="sm" mt={2}>
							<Button
								leftSection={<IconArrowLeft size={16} />}
								variant="subtle"
								color="gray"
								radius="md"
								onClick={onBack}
								size="sm"
								fw={600}
								styles={{
									root: {
										paddingLeft: 10,
										paddingRight: 14,
									},
								}}
							>
								Back to Search
							</Button>
						</Box>
					)}
					<Box ref={containerRef} mah={{ base: '42vh', md: '75vh' }} style={{ width: "100%", aspectRatio: "16 / 9", borderRadius: 20, overflow: "hidden", position: "relative" }}>
						<ReactPlayer
							url={video.url}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								borderRadius: "20px",
								overflow: "hidden",
							}}
							width={"100%"}
							height={"100%"}
							controls
              onEnded={handleOnVideoEnd}
              playing={playing}
							config={{
								file: {
									attributes: {
										referrerPolicy: "strict-origin-when-cross-origin",
										playsInline: true,
									},
								},
							}}
						/>
					</Box>

					<Flex w={"100%"} direction="column" p={{ base: 'xs', sm: 10 }} gap="xs">
						<Flex w={"100%"} direction={"row"} justify={"space-between"} align={"center"} gap="sm">
							<Flex align="center" gap="sm" style={{ minWidth: 0, flex: 1 }}>
								{hasSecondary ? (
									<Avatar.Group>
										<PlayerAvatar
											playerId={primary.id}
											playerName={primary.name}
											league={clipLeague}
											size={40}
											radius="xl"
										/>
										<PlayerAvatar
											playerId={secondary!.id}
											playerName={secondary!.name}
											league={clipLeague}
											size={40}
											radius="xl"
										/>
									</Avatar.Group>
								) : (
									<PlayerAvatar
										playerId={primary.id}
										playerName={primary.name}
										league={clipLeague}
										size={40}
										radius="xl"
									/>
								)}
								<Text fw={700} fz="md" truncate>
									{primary.name} {hasSecondary && secondary?.name ? `w/ ${secondary.name}` : ''}
								</Text>
							</Flex>
							<Flex align="center" gap={{ base: 'xs', sm: 'md' }} style={{ flexShrink: 0 }}>
								<Switch
									styles={{
										body: {
											flex: 1,
											flexDirection: "row-reverse",
										},
										label: {
											paddingRight: 6,
											fontSize: 12,
											fontWeight: 600,
										},
									}}
									size="sm"
									label="Hide Outcomes"
									checked={hideOutcomes}
									onChange={(e) => setHideOutcomes(e.currentTarget.checked)}
								/>
								<Switch
									styles={{
										body: {
											flex: 1,
											flexDirection: "row-reverse",
										},
										label: {
											paddingRight: 6,
											fontSize: 12,
											fontWeight: 600,
										},
									}}
									size="sm"
									label="autoplay"
									checked={autoplay}
									onChange={(e) => setAutoplay(e.target.checked)}
								/>
							</Flex>
						</Flex>

						{/* Row 2: Timing / Game info */}
						<Flex align="center" gap={6} wrap="wrap">
							{dateText && (
								<Badge size="sm" variant="default" radius="sm">
									{dateText}
								</Badge>
							)}
							{matchupScoreText && (
								<Badge
									size="sm"
									variant="default"
									radius="sm"
									className={`outcome-content ${hideOutcomes ? "outcome-blur" : ""}`}
								>
									{matchupScoreText}
								</Badge>
							)}
							<Badge size="sm" variant="default" radius="sm">
								{periodClockText}
							</Badge>
						</Flex>

						{/* Row 3: Video Tags */}
						<Box className={`outcome-content ${hideOutcomes ? "outcome-blur" : ""}`}>
							<EventTagBadges
								video={video}
								targetPlayerId={activeTargetPlayerId}
								targetPlayerName={targetPlayerName}
								size="sm"
							/>
						</Box>
					</Flex>

					<Box p={10}>
						<VideoStats video={video} boxscore={boxscore}/>
					</Box>

					{ activeSearch &&
						<Box w={"100%"} hiddenFrom={"sm"}>
							<Box px="xs" pt="md" pb={8}>
								<Text fz="sm" fw={700} c="dimmed">
									Queue ({total !== null && total !== undefined && total > 0 ? `${total} clips` : `${videos.length} clips`})
								</Text>
							</Box>
							<VideoQueue selectedVideo={video} videos={videos} onSelect={onSelect} total={total} onLoadMore={onLoadMore} hasMore={hasMore} maxHeight={380} targetPlayerId={activeTargetPlayerId} targetPlayerName={targetPlayerName}/>
						</Box>
					}
				</Flex>
			
				{ activeSearch &&
					<Flex flex={2} direction="column" visibleFrom="sm" p={{ base: 'xs', sm: 'md' }}>
						<Box
							style={{
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box mb="sm" mt={2} px={4} style={{ display: "flex", alignItems: "center", minHeight: 36 }}>
								<Text fz="sm" fw={700} c="dimmed">
									Queue ({total !== null && total !== undefined && total > 0 ? `${total} clips` : `${videos.length} clips`})
								</Text>
							</Box>
							<Box style={{ display: "flex", flexDirection: "column" }}>
								<VideoQueue selectedVideo={video} videos={videos} onSelect={onSelect} total={total} onLoadMore={onLoadMore} hasMore={hasMore} maxHeight="72vh" targetPlayerId={activeTargetPlayerId} targetPlayerName={targetPlayerName}/>
							</Box>
						</Box>
					</Flex>
				}
			</Flex> 
	)
}

export default FocusedVideoDisplay